<template>
  <dashboard-layout title="Prompts">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search prompts…" />
      <button class="add-prompt-btn" @click="showAddPromptModal = true">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        New Prompt
      </button>
    </template>

    <stat-grid>
      <stat-card label="Total versions" :value="prompts.length.toLocaleString('en-US')" icon="filetext" accent />
      <stat-card label="Latest version" :value="latestVersion ? `v${latestVersion}` : '—'" icon="zap" />
      <stat-card label="With changelog" :value="changelogCount.toLocaleString('en-US')" icon="clipboardcheck" />
    </stat-grid>

    <data-panel flush>
      <template #title>Prompts <span class="muted">({{ filteredPrompts.length.toLocaleString('en-US') }})</span></template>

      <loading-state v-if="loading" label="Loading prompts…" />

      <empty-state
        v-else-if="filteredPrompts.length === 0"
        icon="filetext"
        :title="prompts.length === 0 ? 'No prompts found' : 'No matching prompts'"
        :hint="prompts.length === 0 ? 'Create your first prompt to get started.' : 'Try a different search term.'"
      />

      <div v-else class="table-scroll-wrapper">
        <table class="prompts-table">
          <thead>
            <tr>
              <th @click="sortBy('version')" class="sortable">
                Version
                <span class="sort-arrow" v-if="sortKey === 'version'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
              </th>
              <th @click="sortBy('createdAt')" class="sortable">
                Created At
                <span class="sort-arrow" v-if="sortKey === 'createdAt'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
              </th>
              <th>Changelog</th>
              <th>Value</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="prompt in filteredPrompts"
              :key="prompt.id"
              class="prompt-row"
              @click="openPromptDetail(prompt)"
            >
              <td class="version-cell">
                <status-badge variant="primary" :label="`v${prompt.version || 'N/A'}`" />
              </td>
              <td>{{ formatCreatedAt(prompt.createdAt) }}</td>
              <td class="changelog-cell">
                <div class="changelog-preview" v-if="prompt.changelog">{{ truncateValue(prompt.changelog, 80) }}</div>
                <span v-else class="no-changelog">—</span>
              </td>
              <td class="prompt-value-cell">
                <div class="prompt-preview">
                  {{ truncateValue(prompt.value) }}
                </div>
              </td>
              <td class="actions-cell">
                <button class="delete-btn" @click.stop="deletePrompt(prompt.id)">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </data-panel>

    <!-- Add Prompt Modal -->
      <div v-if="showAddPromptModal" class="modal-overlay" @click.self="cancelAddPrompt">
        <div class="modal-content">
          <h3>Add New Prompt</h3>
          <textarea
            v-model="newPromptValue"
            placeholder="Enter prompt value..."
            rows="6"
            class="prompt-textarea"
          ></textarea>
          <textarea
            v-model="newPromptChangelog"
            placeholder="What changed? (e.g. Prediction kuralı güçlendirildi, Section 5...)"
            rows="4"
            class="prompt-textarea changelog-textarea"
          ></textarea>
          <div class="toggle-container">
            <label for="force-update-toggle">Force Update</label>
            <label class="switch">
              <input id="force-update-toggle" type="checkbox" v-model="newPromptForceUpdate">
              <span class="slider round"></span>
            </label>
          </div>
          <div class="modal-actions">
            <button class="cancel-btn" @click="cancelAddPrompt">Cancel</button>
            <button
              class="save-btn"
              @click="savePrompt"
              :disabled="!newPromptValue.trim() || saving"
            >
              {{ saving ? 'Saving...' : 'Save Prompt' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Prompt Detail Modal -->
      <div v-if="selectedPrompt" class="modal-overlay" @click.self="closePromptDetail">
        <div class="modal-content detail-modal">
          <div class="detail-header">
            <h3>Prompt Detail</h3>
            <status-badge variant="primary" :label="`v${selectedPrompt.version || 'N/A'}`" />
          </div>
          <div class="detail-meta">
            <span class="meta-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              {{ formatCreatedAt(selectedPrompt.createdAt) }}
            </span>
            <status-badge v-if="selectedPrompt.forceUpdate" variant="success" label="Force Update" />
          </div>
          <div v-if="selectedPrompt.changelog" class="detail-changelog">
            <div class="section-header">
              <h4>Changelog</h4>
              <button class="copy-btn" @click="copyToClipboard(selectedPrompt.changelog, 'changelog')">
                <svg v-if="copiedKey !== 'changelog'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {{ copiedKey === 'changelog' ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <pre class="changelog-content">{{ selectedPrompt.changelog }}</pre>
          </div>
          <div class="detail-section">
            <div class="section-header">
              <div class="section-tabs">
                <button :class="['tab-btn', { active: viewMode === 'value' }]" @click="viewMode = 'value'">Value</button>
                <button
                  :class="['tab-btn', { active: viewMode === 'diff' }]"
                  @click="viewMode = 'diff'"
                  :disabled="!previousPrompt"
                  :title="previousPrompt ? `Compare with v${previousPrompt.version}` : 'No previous version'"
                >
                  Diff{{ previousPrompt ? ` vs v${previousPrompt.version}` : '' }}
                </button>
              </div>
              <button class="copy-btn" @click="copyToClipboard(selectedPrompt.value, 'value')">
                <svg v-if="copiedKey !== 'value'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {{ copiedKey === 'value' ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <div v-if="viewMode === 'value'" class="detail-content">
              <pre>{{ selectedPrompt.value }}</pre>
            </div>
            <div v-else class="detail-content diff-view">
              <div class="diff-stats">
                <span class="diff-stat add">+{{ diffStats.added }}</span>
                <span class="diff-stat remove">−{{ diffStats.removed }}</span>
              </div>
              <div class="diff-lines">
                <div
                  v-for="(line, idx) in diffLines"
                  :key="idx"
                  :class="['diff-line', `diff-${line.type}`]"
                >
                  <span class="diff-marker">{{ line.type === 'add' ? '+' : line.type === 'remove' ? '−' : ' ' }}</span>
                  <span class="diff-text">{{ line.text }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-actions">
            <button class="cancel-btn" @click="closePromptDetail">Close</button>
            <button class="delete-btn" @click="deletePromptFromDetail">Delete</button>
          </div>
        </div>
      </div>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, addDoc, query, orderBy, getDocs, serverTimestamp, limit, doc, deleteDoc } from 'firebase/firestore'
import { initFirebase } from '../firebase'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import SearchBox from './ui/SearchBox.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'
import StatusBadge from './ui/StatusBadge.vue'

export default {
  name: 'PromptsView',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, SearchBox, LoadingState, EmptyState, StatusBadge
  },
  setup() {
    const { db } = initFirebase()
    const prompts = ref([])
    const loading = ref(true)
    const searchQuery = ref('')
    const showAddPromptModal = ref(false)
    const newPromptValue = ref('')
    const newPromptChangelog = ref('')
    const saving = ref(false)
    const newPromptForceUpdate = ref(false)
    const selectedPrompt = ref(null)
    const sortKey = ref('version')
    const sortDirection = ref(-1)
    const viewMode = ref('value')
    const copiedKey = ref(null)
    let copyResetTimer = null

    const copyToClipboard = async (text, key) => {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        copiedKey.value = key
        if (copyResetTimer) clearTimeout(copyResetTimer)
        copyResetTimer = setTimeout(() => {
          copiedKey.value = null
        }, 1500)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const sortedPrompts = computed(() => {
      const sorted = [...prompts.value]
      if (sortKey.value) {
        sorted.sort((a, b) => {
          let valA, valB
          switch (sortKey.value) {
            case 'version':
              valA = a.version || 0
              valB = b.version || 0
              break
            case 'createdAt':
              valA = a.createdAt?.seconds || 0
              valB = b.createdAt?.seconds || 0
              break
            default:
              return 0
          }
          return (valA - valB) * sortDirection.value
        })
      }
      return sorted
    })

    const sortBy = (key) => {
      if (sortKey.value === key) {
        sortDirection.value *= -1
      } else {
        sortKey.value = key
        sortDirection.value = -1
      }
    }

    const filteredPrompts = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      if (!q) return sortedPrompts.value
      return sortedPrompts.value.filter(p => {
        const hay = `v${p.version || ''} ${p.value || ''} ${p.changelog || ''}`.toLowerCase()
        return hay.includes(q)
      })
    })

    const latestVersion = computed(() =>
      prompts.value.reduce((max, p) => Math.max(max, p.version || 0), 0)
    )

    const changelogCount = computed(() =>
      prompts.value.filter(p => p.changelog && p.changelog.trim()).length
    )

    const truncateValue = (value, maxLen = 100) => {
      if (!value) return '-'
      if (value.length > maxLen) {
        return value.substring(0, maxLen) + '...'
      }
      return value
    }

    const openPromptDetail = (prompt) => {
      selectedPrompt.value = prompt
      viewMode.value = 'value'
    }

    const closePromptDetail = () => {
      selectedPrompt.value = null
    }

    const previousPrompt = computed(() => {
      if (!selectedPrompt.value) return null
      const currentVersion = selectedPrompt.value.version
      if (currentVersion == null) return null
      const candidates = prompts.value.filter(p => (p.version || 0) < currentVersion)
      if (candidates.length === 0) return null
      return candidates.reduce((best, p) => (p.version > (best?.version || 0) ? p : best), null)
    })

    const diffLineByLine = (oldText, newText) => {
      const oldLines = (oldText || '').split('\n')
      const newLines = (newText || '').split('\n')
      const m = oldLines.length
      const n = newLines.length
      const lcs = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          lcs[i][j] = oldLines[i - 1] === newLines[j - 1]
            ? lcs[i - 1][j - 1] + 1
            : Math.max(lcs[i - 1][j], lcs[i][j - 1])
        }
      }
      const result = []
      let i = m, j = n
      while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
          result.unshift({ type: 'context', text: oldLines[i - 1] })
          i--
          j--
        } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
          result.unshift({ type: 'add', text: newLines[j - 1] })
          j--
        } else {
          result.unshift({ type: 'remove', text: oldLines[i - 1] })
          i--
        }
      }
      return result
    }

    const diffLines = computed(() => {
      if (!selectedPrompt.value || !previousPrompt.value) return []
      return diffLineByLine(previousPrompt.value.value, selectedPrompt.value.value)
    })

    const diffStats = computed(() => {
      const lines = diffLines.value
      return {
        added: lines.filter(l => l.type === 'add').length,
        removed: lines.filter(l => l.type === 'remove').length
      }
    })

    const deletePromptFromDetail = async () => {
      if (selectedPrompt.value) {
        await deletePrompt(selectedPrompt.value.id)
        closePromptDetail()
      }
    }

    const loadPrompts = async () => {
      try {
        const promptsRef = collection(db, 'prompts')
        const q = query(promptsRef, orderBy('createdAt', 'desc'))
        const querySnapshot = await getDocs(q)

        prompts.value = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('Error loading prompts:', error)
      } finally {
        loading.value = false
      }
    }

    const savePrompt = async () => {
      if (!newPromptValue.value.trim() || saving.value) return

      saving.value = true
      try {
        const promptsRef = collection(db, 'prompts')

        const q = query(promptsRef, orderBy('version', 'desc'), limit(1))
        const querySnapshot = await getDocs(q)

        let latestVersion = 0
        if (!querySnapshot.empty) {
          latestVersion = querySnapshot.docs[0].data().version || 0
        }

        const newVersion = latestVersion + 1

        const docData = {
          value: newPromptValue.value.trim(),
          createdAt: serverTimestamp(),
          version: newVersion,
          forceUpdate: newPromptForceUpdate.value
        }
        if (newPromptChangelog.value.trim()) {
          docData.changelog = newPromptChangelog.value.trim()
        }

        await addDoc(promptsRef, docData)

        await loadPrompts()
        showAddPromptModal.value = false
        newPromptValue.value = ''
        newPromptChangelog.value = ''
        newPromptForceUpdate.value = false
      } catch (error) {
        console.error('Error saving prompt:', error)
      } finally {
        saving.value = false
      }
    }

    const deletePrompt = async (promptId) => {
      if (confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
        try {
          await deleteDoc(doc(db, 'prompts', promptId));
          await loadPrompts();
        } catch (error) {
          console.error('Error deleting prompt:', error);
          alert('Failed to delete prompt.');
        }
      }
    }

    const cancelAddPrompt = () => {
      showAddPromptModal.value = false
      newPromptValue.value = ''
      newPromptChangelog.value = ''
      newPromptForceUpdate.value = false
    }

    const formatCreatedAt = (timestamp) => {
      if (!timestamp) return '-'
      const date = new Date(timestamp.seconds * 1000)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    onMounted(() => {
      loadPrompts()
    })

    return {
      prompts,
      loading,
      searchQuery,
      showAddPromptModal,
      newPromptChangelog,
      newPromptValue,
      saving,
      filteredPrompts,
      latestVersion,
      changelogCount,
      savePrompt,
      cancelAddPrompt,
      formatCreatedAt,
      truncateValue,
      newPromptForceUpdate,
      deletePrompt,
      selectedPrompt,
      openPromptDetail,
      closePromptDetail,
      deletePromptFromDetail,
      sortKey,
      sortDirection,
      sortBy,
      copiedKey,
      copyToClipboard,
      viewMode,
      previousPrompt,
      diffLines,
      diffStats
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }

.add-prompt-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-prompt-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
}

.table-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
}

.prompts-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.prompts-table th,
.prompts-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.prompts-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(26, 27, 46, 0.98);
  height: 44px;
}

.prompts-table th.sortable {
  cursor: pointer;
  transition: color 0.2s ease;
  user-select: none;
}

.prompts-table th.sortable:hover {
  color: var(--text-primary);
}

.prompts-table th .sort-arrow {
  font-size: 10px;
  margin-left: 4px;
  display: inline-block;
  opacity: 0.8;
}

.prompts-table td {
  color: var(--text-primary);
  font-size: 13px;
}

.prompt-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.prompt-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

.version-cell {
  width: 80px;
}

.changelog-cell {
  max-width: 200px;
}
.changelog-preview {
  font-size: 12px;
  color: #a29bfe;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.no-changelog {
  color: var(--text-secondary);
  opacity: 0.4;
}
.changelog-textarea {
  border-left: 3px solid #a29bfe !important;
}
.detail-changelog {
  margin-bottom: 16px;
}
.detail-changelog h4 {
  font-size: 14px;
  color: #a29bfe;
  margin: 0;
}
.detail-section {
  margin-bottom: 16px;
}
.detail-section h4 {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.copy-btn:hover {
  background: rgba(108, 99, 255, 0.15);
  border-color: rgba(108, 99, 255, 0.4);
  color: var(--text-primary);
}
.copy-btn:active {
  transform: scale(0.97);
}
.section-tabs {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  padding: 2px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.tab-btn {
  padding: 5px 12px;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.tab-btn:hover:not(:disabled):not(.active) {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.05);
}
.tab-btn.active {
  background: var(--primary-color);
  color: white;
}
.tab-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.diff-view {
  padding: 0;
}
.diff-stats {
  display: flex;
  gap: 12px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: monospace;
  font-size: 12px;
  font-weight: 600;
}
.diff-stat.add {
  color: #34d399;
}
.diff-stat.remove {
  color: #f87171;
}
.diff-lines {
  font-family: monospace;
  font-size: 12px;
  line-height: 1.55;
  padding: 8px 0;
}
.diff-line {
  display: flex;
  padding: 0 16px;
  white-space: pre-wrap;
  word-break: break-word;
}
.diff-line.diff-context {
  color: rgba(255, 255, 255, 0.5);
}
.diff-line.diff-add {
  background: rgba(34, 197, 94, 0.12);
  color: #86efac;
}
.diff-line.diff-remove {
  background: rgba(239, 68, 68, 0.12);
  color: #fca5a5;
}
.diff-marker {
  flex-shrink: 0;
  width: 18px;
  user-select: none;
  opacity: 0.7;
}
.diff-text {
  flex: 1;
}
.changelog-content {
  font-size: 13px;
  line-height: 1.6;
  color: rgba(255,255,255,0.7);
  background: rgba(162,155,254,0.06);
  border: 1px solid rgba(162,155,254,0.15);
  border-radius: 8px;
  padding: 12px 16px;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
}
.prompt-value-cell {
  max-width: 500px;
}

.prompt-preview {
  font-family: monospace;
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.actions-cell {
  width: 80px;
  text-align: center;
}

.delete-btn {
  background: none;
  border: 1px solid rgba(239, 68, 68, 0.5);
  color: rgba(239, 68, 68, 0.8);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  border-color: #ef4444;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 15, 26, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: rgba(26, 27, 46, 0.98);
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.modal-content.detail-modal {
  max-width: 800px;
}

.modal-content h3 {
  margin: 0 0 16px 0;
  color: var(--text-primary);
  font-size: 18px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.detail-header h3 {
  margin: 0;
}

.detail-meta {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 13px;
}

.detail-content {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  max-height: 50vh;
  overflow-y: auto;
}

.detail-content pre {
  margin: 0;
  font-family: monospace;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-primary);
  white-space: pre-wrap;
  word-break: break-word;
}

.toggle-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  background: rgba(255, 255, 255, 0.05);
  padding: 12px;
  border-radius: 8px;
}

.toggle-container label {
  color: var(--text-secondary);
  font-size: 14px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.1);
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: var(--primary-color);
}

input:focus + .slider {
  box-shadow: 0 0 1px var(--primary-color);
}

input:checked + .slider:before {
  transform: translateX(22px);
}

.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.prompt-textarea {
  width: 100%;
  min-height: 200px;
  max-height: 50vh;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  color: var(--text-primary);
  font-size: 14px;
  font-family: monospace;
  line-height: 1.5;
  resize: vertical;
  margin-bottom: 16px;
  overflow-y: auto;
}

.prompt-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-actions button {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
}

.save-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  border: none;
  color: white;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
}

.save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Scrollbar styles */
.table-scroll-wrapper::-webkit-scrollbar,
.modal-content::-webkit-scrollbar,
.prompt-textarea::-webkit-scrollbar,
.detail-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track,
.modal-content::-webkit-scrollbar-track,
.prompt-textarea::-webkit-scrollbar-track,
.detail-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb,
.modal-content::-webkit-scrollbar-thumb,
.prompt-textarea::-webkit-scrollbar-thumb,
.detail-content::-webkit-scrollbar-thumb {
  background: rgba(108, 99, 255, 0.5);
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover,
.modal-content::-webkit-scrollbar-thumb:hover,
.prompt-textarea::-webkit-scrollbar-thumb:hover,
.detail-content::-webkit-scrollbar-thumb:hover {
  background: rgba(108, 99, 255, 0.7);
}

@media (max-width: 768px) {
  .prompt-value-cell {
    max-width: 200px;
  }

  .modal-content {
    width: 95%;
    padding: 16px;
  }
}
</style>
